<template>
  <div class="page" v-if="lead">
    <button class="btn secondary" @click="$router.back()">← Back</button>

    <div class="row" style="margin-top:12px; align-items:center;">
      <h2 style="margin:0; margin-right:8px;">{{ lead.name }}</h2>

      <span
        class="badge"
        :class="isInquiry ? 'badge--green' : (isDead ? 'badge--red' : 'badge--blue')"
      >
        {{ isInquiry ? 'INQUIRY' : (isDead ? 'DEAD' : 'LEAD') }}
      </span>

      <span class="badge" style="margin-left:6px;">ID: {{ lead.leadCode || lead.id }}</span>
    </div>

    <div class="helper">{{ lead.phone }} <span v-if="lead.phone && lead.email">•</span> {{ lead.email }}</div>
    <div class="helper" v-if="lead.address">{{ lead.address }}</div>

    <div class="helper" style="margin-top:4px;">
      Products: <b>{{ productsText(lead.products) }}</b>
      <span v-if="lead.cause"> • Source: {{ causeText(lead) }}</span>
    </div>

    <!-- LEAD: follow-up banner -->
    <div
      class="card"
      v-if="isLead"
      :style="{marginTop:'14px', borderColor: isOverdue ? '#dc2626' : '#e5e7eb'}"
    >
      <h3 :style="{color: isOverdue ? '#dc2626' : '#111827'}">
        {{ isOverdue ? 'Follow-up overdue' : 'Follow-up scheduled' }}
      </h3>

      <p class="helper" v-if="lead.followUpDate">
        Next follow-up: <b>{{ lead.followUpDate }}</b>
        <span v-if="isOverdue" style="color:#dc2626;"> • Please take action</span>
      </p>

      <div class="helper" style="margin-top:6px;">
        You can upload Site Photos + Measurements + Drawings before converting. Once converted, you’ll have <b>24 hours</b> to complete required docs.
      </div>

      <div v-if="convertErr" class="helper" style="color:#dc2626; margin-top:8px;">
        {{ convertErr }}
      </div>

      <div class="row" style="gap:8px; margin-top:10px;">
        <button class="btn" :disabled="converting" @click="convertToInquiry">
          {{ converting ? 'Converting…' : 'Convert to Inquiry' }}
        </button>
        <button class="btn secondary" @click="openEdit = true">Reschedule / Edit</button>
      </div>
    </div>

    <!-- DEAD -->
    <div class="card" v-if="isDead" style="margin-top:14px;">
      <h3>Lead marked Dead</h3>
      <p class="helper">Reason: {{ deadText(lead) }}</p>
      <div class="row" style="gap:8px; margin-top:8px;">
        <button class="btn secondary" @click="openEdit = true">Edit</button>
      </div>
    </div>

    <!-- INQUIRY -->
    <div class="card" v-if="isInquiry" style="margin-top:14px;">
      <div class="row" style="align-items:center;">
        <div>
          <h3 style="margin:0;">Inquiry</h3>
          <div class="helper">Required docs should be uploaded within 24 hours of conversion.</div>
        </div>

        <div style="margin-left:auto;text-align:right;">
          <div class="badge" :class="docsOk ? 'badge--green' : (docsOverdue ? 'badge--red' : 'badge--amber')">
            {{ docsOk ? 'DOCS COMPLETE' : (docsOverdue ? 'DOCS OVERDUE' : 'DOCS PENDING') }}
          </div>

          <div class="helper" v-if="inquiryCreatedAt">
            Created: {{ fmtDateTime(inquiryCreatedAt) }}
          </div>

          <div class="helper" v-if="!docsOk && docsDueAt">
            Deadline: {{ fmtDateTime(docsDueAt) }}
          </div>
        </div>
      </div>

      <div class="helper" style="margin-top:8px;">
        Photos: <b>{{ docsSitePhotos }}</b>
        • Measurements: <b>{{ docsMeasurements }}</b>
        • Drawings: <b>{{ docsDrawingsTotal }}</b>

      </div>

      <div class="row" style="gap:8px; margin-top:10px;">
        <button
          class="btn"
          :disabled="!docsOk"
          :title="!docsOk ? 'Upload Site Photos + Measurements + Drawings before creating quotations' : ''"
          @click="docsOk ? $router.push(toQuote()) : null"
        >
          + Create Quotation
        </button>
        <button class="btn secondary" @click="openEdit = true">Edit Inquiry</button>
      </div>

      <div v-if="!docsOk" class="helper" style="color:#dc2626;margin-top:8px;">
        Quotations are blocked until required docs are uploaded.
        <span v-if="docsOverdue"> This inquiry is now <b>overdue</b>.</span>
      </div>
    </div>

    <!-- Site Docs + Attachments (allowed for lead + inquiry) -->
    <div v-if="isLead || isInquiry" class="card" style="margin-top:14px;">
      <div class="row" style="align-items:center;">
        <div>
          <h3 style="margin:0;">Site Docs</h3>
          <div class="helper" v-if="isLead">
            Uploading a doc will auto-convert to inquiry and start the 24h timer
          </div>
          <div class="helper" v-else>
            Keep docs updated here (deadline tracked automatically).
          </div>
        </div>
        <button class="btn secondary small" style="margin-left:auto;" @click="refreshDocs">Refresh</button>
      </div>

      <div class="helper" style="margin-top:8px;">
        Photos: <b>{{ docsSitePhotos }}</b>
        • Measurements: <b>{{ docsMeasurements }}</b>
        • Drawings: <b>{{ docsDrawingsTotal }}</b>


        <span style="margin-left:8px;" class="badge" :class="docsOk ? 'badge--green' : (docsOverdue ? 'badge--red' : 'badge--amber')">
          {{ docsOk ? 'READY' : (docsOverdue ? 'OVERDUE' : 'PENDING') }}
        </span>

        <span v-if="!docsOk && docsDueAt && isInquiry" class="helper" style="margin-left:8px;">
          Deadline: <b>{{ fmtDateTime(docsDueAt) }}</b>
        </span>
      </div>

      <!-- SITE IMAGE -->
      <div style="margin-top:10px;">
        <h4 style="margin:0 0 6px;">Site Photos (required)</h4>
        <div class="row" style="gap:8px;align-items:center;">
          <input type="file" @change="pickFile($event,'SITE_IMAGE')" accept=".png,.jpg,.jpeg,.webp,.pdf">
          <button class="btn small" :disabled="!sel.SITE_IMAGE" @click="upload('SITE_IMAGE')">Upload</button>
        </div>

        <ul class="helper" v-if="photos.length" style="margin-top:6px;">
          <li v-for="d in photos" :key="d.id">
            <a :href="downloadUrl(d.id)" target="_blank" rel="noopener">{{ d.fileName }}</a>
            <span class="mono"> • {{ fmtDateTime(d.createdAt) }}</span>
          </li>
        </ul>
        <div v-else class="helper">No site photos uploaded.</div>
      </div>

      <!-- SITE MEASUREMENT -->
      <div style="margin-top:12px;">
        <h4 style="margin:0 0 6px;">Measurements (required)</h4>
        <div class="row" style="gap:8px;align-items:center;">
          <input type="file" @change="pickFile($event,'SITE_MEASUREMENT')" accept=".pdf,.png,.jpg,.jpeg,.webp">
          <button class="btn small" :disabled="!sel.SITE_MEASUREMENT" @click="upload('SITE_MEASUREMENT')">Upload</button>
        </div>
        <ul class="helper" v-if="measurementsList.length" style="margin-top:6px;">
          <li v-for="d in measurementsList" :key="d.id">
            <a :href="downloadUrl(d.id)" target="_blank" rel="noopener">{{ d.fileName }}</a>
            <span class="mono"> • {{ fmtDateTime(d.createdAt) }}</span>
          </li>
        </ul>
        <div v-else class="helper">No measurement docs uploaded.</div>
      </div>

      <!-- DRAWING 3D -->
      <div style="margin-top:12px;">
        <h4 style="margin:0 0 6px;">3D Renders (required: 2D OR 3D)</h4>
        <div class="row" style="gap:8px;align-items:center;">
          <input type="file" @change="pickFile($event,'DRAWING_3D')" accept=".pdf,.png,.jpg,.jpeg,.webp">
          <button class="btn small" :disabled="!sel.DRAWING_3D" @click="upload('DRAWING_3D')">Upload</button>
        </div>
        <ul class="helper" v-if="drawings3d.length" style="margin-top:6px;">
          <li v-for="d in drawings3d" :key="d.id">
            <a :href="downloadUrl(d.id)" target="_blank" rel="noopener">{{ d.fileName }}</a>
            <span class="mono"> • {{ fmtDateTime(d.createdAt) }}</span>
          </li>
        </ul>
        <div v-else class="helper">No 3D renders uploaded.</div>
      </div>

      <!-- DRAWING 2D -->
      <div style="margin-top:12px;">
        <h4 style="margin:0 0 6px;">2D Drawings (required: 2D OR 3D)</h4>
        <div class="row" style="gap:8px;align-items:center;">
          <input type="file" @change="pickFile($event,'DRAWING_2D')" accept=".pdf,.png,.jpg,.jpeg,.svg,.webp">
          <button class="btn small" :disabled="!sel.DRAWING_2D" @click="upload('DRAWING_2D')">Upload</button>
        </div>
        <ul class="helper" v-if="drawings2d.length" style="margin-top:6px;">
          <li v-for="d in drawings2d" :key="d.id">
            <a :href="downloadUrl(d.id)" target="_blank" rel="noopener">{{ d.fileName }}</a>
            <span class="mono"> • {{ fmtDateTime(d.createdAt) }}</span>
          </li>
        </ul>
        <div v-else class="helper">No 2D drawings uploaded.</div>
      </div>

      <div v-if="uploadFlash" class="helper" style="margin-top:10px;">
        {{ uploadFlash }}
      </div>
    </div>

    <customer-form
      v-if="openEdit"
      :value="leadForForm"
      @close="openEdit = false"
      @saved="afterEdit"
    />
  </div>
</template>

<script>
import CustomerForm from '@/components/customers/CustomerForm.vue'

export default {
  components:{ CustomerForm },
  data(){
    return {
      lead:null,
      openEdit:false,

      // inquiry resolution
      inquiryId: null,
      inquiryCreatedAt: null,
      docsDueAt: null,
      uploading: false,

      docs: {
        sitePhotos:0,
        measurements:0,
        drawings2d:0,
        drawings3d:0,
        docsOk:false,
        docsOverdue:false,
        docsState:null
      },

      attachments: [],
      sel: { SITE_IMAGE:null, SITE_MEASUREMENT:null, DRAWING_3D:null, DRAWING_2D:null },
      uploadFlash:'',
      converting:false,
      convertErr:''
    }
  },
  computed:{
    isDead(){
      return (this.lead && String(this.lead.status || '').toLowerCase() === 'dead')
    },
    isInquiry(){
      // inquiry exists => show inquiry UI (new architecture)
      return !!this.inquiryId
    },
    isLead(){
      return !!this.lead && !this.isDead && !this.isInquiry
    },

    leadForForm(){
      if (!this.lead) return null
      var v = Object.assign({}, this.lead)
      if (v && typeof v.products === 'string') {
        v.products = v.products.split(',').map(function(x){return x.trim()}).filter(Boolean)
      }
      return v
    },

    docsOk(){ return !!(this.docs && this.docs.docsOk) },
    docsOverdue(){ return !!(this.docs && this.docs.docsOverdue) },

    photos(){
      return (this.attachments||[]).filter(function(a){ return a.docType === 'SITE_IMAGE' })
    },
    measurementsList(){
      return (this.attachments||[]).filter(function(a){ return a.docType === 'SITE_MEASUREMENT' })
    },
    drawings3d(){
      return (this.attachments||[]).filter(function(a){ return a.docType === 'DRAWING_3D' })
    },
    drawings2d(){
      return (this.attachments||[]).filter(function(a){ return a.docType === 'DRAWING_2D' })
    },

    isOverdue(){
      if (!(this.lead && this.lead.followUpDate)) return false
      var t = new Date()
      var d = new Date(this.lead.followUpDate)
      var d1 = new Date(t.getFullYear(), t.getMonth(), t.getDate())
      var d2 = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      return d2 < d1
    },

    docsSitePhotos: function(){ return this.docs && this.docs.sitePhotos ? this.docs.sitePhotos : 0 },
    docsMeasurements: function(){ return this.docs && this.docs.measurements ? this.docs.measurements : 0 },
    docsDrawings2d: function(){ return this.docs && this.docs.drawings2d ? this.docs.drawings2d : 0 },
    docsDrawings3d: function(){ return this.docs && this.docs.drawings3d ? this.docs.drawings3d : 0 },
    docsDrawingsTotal: function(){ return this.docsDrawings2d + this.docsDrawings3d },

  },
  async created(){ await this.hydrate() },
  watch:{ '$route.params.id': { handler(){ this.hydrate() } } },
  methods:{
    productsText(csv){ if (!csv) return '—'; return (typeof csv==='string' ? csv : (csv||[]).join(',')).replace(/,/g,' • ') },
    causeText(c){ if (!c) return '—'; if (c.cause==='other') return c.causeOther || 'Other'; return (c.cause||'').replace(/_/g,' ') },
    deadText(c){
      if (c.deadReason==='expensive') return 'Expensive / price too high'
      if (c.deadReason==='range') return 'Not enough product range'
      if (c.deadReason==='time') return 'Time constraint'
      if (c.deadReason==='other') return c.deadOther || 'Other'
      return c.deadReason || '—'
    },
    fmtDateTime(d){
      if (!d) return '—'
      try { return new Date(d).toLocaleString() } catch(e){ return String(d) }
    },

    downloadUrl(aid){
      // uses your backend download endpoint
      return (this.$api && this.$api.defaults && this.$api.defaults.baseURL)
        ? (this.$api.defaults.baseURL.replace(/\/$/, '') + '/inquiries/attachments/' + aid + '/download')
        : ('/api/inquiries/attachments/' + aid + '/download')
    },

    async hydrate(){
      var id = this.$route.params.id
      if (!id) return

      this.convertErr = ''
      this.uploadFlash = ''
      this.sel = { SITE_IMAGE:null, SITE_MEASUREMENT:null, DRAWING_3D:null, DRAWING_2D:null }
      this.inquiryId = null
      this.inquiryCreatedAt = null
      this.docsDueAt = null

      // Load lead (kept compatible with your existing pattern)
      try{
        var res = await this.$api.get('/leads/' + id)
        this.lead = res && res.data ? res.data : null
      } catch(e){
        this.lead = null
      }


      if (!this.lead) return

      // Resolve inquiry by listing inquiries and matching leadId (no new endpoint required)
      await this.resolveInquiry()

      // Load docs + attachments (works for lead + inquiry)
      await Promise.allSettled([
        this.loadDocs(),
        this.loadAttachments()
      ])
    },

    async resolveInquiry(){
      this.inquiryId = null
      this.inquiryCreatedAt = null
      this.docsDueAt = null
      try{
        const { data } = await this.$api.get('/inquiries')
        const list = Array.isArray(data) ? data : []
        const hit = list.find((x) => String(x.leadId) === String(this.lead.id))
        if (hit && hit.id){
          this.inquiryId = hit.id
          this.inquiryCreatedAt = hit.createdAt || null
          this.docsDueAt = hit.docsDueAt || null
        }
      } catch(e){
        // ignore
      }
    },

    async refreshDocs(){
      await Promise.allSettled([ this.resolveInquiry(), this.loadDocs(), this.loadAttachments() ])
    },

    async convertToInquiry(){
      if (!this.lead) return
      this.convertErr = ''
      this.converting = true
      try{
        await this.$api.post('/inquiries/convert', { leadId: this.lead.id })
        await this.hydrate()
      } catch(e){
        const r = e && e.response && e.response.data ? e.response.data : null
        if (r && r.error === 'forbidden') this.convertErr = 'Forbidden'
        else this.convertErr = 'Conversion failed. Please try again.'
      } finally{
        this.converting = false
      }
    },

    async afterEdit(){ this.openEdit=false; await this.hydrate() },

    async loadDocs(){
      // For inquiry docs summary, backend gives it via GET /inquiries/<iid>
      // For leads (not converted), we still can compute from attachments list later,
      // but backend summary is inquiry-based, so keep docs "pending" until inquiry exists.
      var empty = {
        sitePhotos:0, measurements:0, drawings2d:0, drawings3d:0,
        docsOk:false, docsOverdue:false, docsState:null
      }

      if (!this.lead || this.isDead) {
        this.docs = empty
        return
      }

      if (!this.inquiryId){
        // Not converted yet: no docs exist because uploads auto-convert to inquiry
        this.docs = Object.assign({}, empty, { docsState: 'PENDING' })
        return
      }


      try{
        const { data } = await this.$api.get('/inquiries/' + this.inquiryId)
        const docs = data && data.docs ? data.docs : null

        const counts = (docs && docs.counts) ? docs.counts : {}
        const sitePhotos = Number(counts.SITE_IMAGE || 0)
        const measurements = Number(counts.SITE_MEASUREMENT || 0)
        const drawings2d = Number(counts.DRAWING_2D || 0)
        const drawings3d = Number(counts.DRAWING_3D || 0)

        this.docs = {
          sitePhotos: sitePhotos,
          measurements: measurements,
          drawings2d: drawings2d,
          drawings3d: drawings3d,
          docsOk: !!(docs && docs.docsOk),
          docsOverdue: !!(docs && docs.docsOverdue),
          docsState: (docs && docs.docsState) ? docs.docsState : null
        }

        // keep top-right dates synced from inquiry payload if present
        const inq = data && data.inquiry ? data.inquiry : null
        if (inq){
          this.inquiryCreatedAt = inq.createdAt || this.inquiryCreatedAt
          this.docsDueAt = inq.docsDueAt || this.docsDueAt
        }
      } catch(e){
        this.docs = empty
      }
    },

    // countFromAttachments(){
    //   const list = (this.attachments || [])
    //   const out = { sitePhotos:0, measurements:0, drawings2d:0, drawings3d:0 }
    //   for (let i=0;i<list.length;i++){
    //     const a = list[i]
    //     if (!a || !a.docType) continue
    //     const t = String(a.docType).toUpperCase()
    //     if (t === 'SITE_IMAGE') out.sitePhotos++
    //     else if (t === 'SITE_MEASUREMENT') out.measurements++
    //     else if (t === 'DRAWING_2D') out.drawings2d++
    //     else if (t === 'DRAWING_3D') out.drawings3d++
    //   }
    //   // docsOk rule (same as backend): meas>0 AND img>0 AND (d2+d3)>0
    //   const ok = (out.measurements > 0) && (out.sitePhotos > 0) && ((out.drawings2d + out.drawings3d) > 0)
    //   return Object.assign(out, { docsOk: ok, docsOverdue: false, docsState: ok ? 'COMPLETE' : 'PENDING' })
    // },

    async loadAttachments(){
      this.uploadFlash = ''
      if (!this.lead || this.isDead) { this.attachments=[]; return }

      if (!this.inquiryId){
        // No inquiry yet => attachments list endpoint is inquiry-based, so show empty for now
        this.attachments = []
        return
      }

      try{
        const { data } = await this.$api.get('/inquiries/' + this.inquiryId + '/attachments')
        this.attachments = Array.isArray(data) ? data : []
      } catch(e){
        this.attachments = []
      }
    },

    pickFile(ev, type){
      const f = ev.target.files && ev.target.files[0]
      this.sel[type] = f || null
      this.uploadFlash = ''
    },

    async upload(type){
      if (!this.lead || this.isDead) return

      if (this.uploading) return
      this.uploading = true

      try {
        if (!this.inquiryId){
          try {
            await this.$api.post('/inquiries/convert', { leadId: this.lead.id })
          } catch (e) {}
          await this.resolveInquiry()
          await Promise.allSettled([ this.loadAttachments(), this.loadDocs() ])
        }
        if (!this.inquiryId) return

        const f = this.sel[type]
        if (!f) return

        const toDataUrl = file => new Promise((res,rej)=>{
          const r = new FileReader()
          r.onload=()=>res(r.result)
          r.onerror=rej
          r.readAsDataURL(file)
        })

        this.uploadFlash = 'Uploading...'
        const dataUrl = await toDataUrl(f)

        await this.$api.post('/inquiries/' + this.inquiryId + '/attachments', {
          dataUrl,
          filename: f.name,
          docType: type
        })

        this.uploadFlash = 'Uploaded ✓'
        this.sel[type] = null
        await Promise.allSettled([ this.loadAttachments(), this.loadDocs() ])
      } catch (e) {
        const r = e && e.response && e.response.data ? e.response.data : null
        this.uploadFlash = (r && r.error) ? ('Upload failed: ' + r.error) : (e._eddaMsg ? ('Upload failed: ' + e._eddaMsg) : 'Upload failed')
      } finally {
        this.uploading = false
      }
    },

    toQuote: function(){
      return {
        name: 'sales-quotations',
        query: {
          inquiryId: String(this.inquiryId),
          leadId: String(this.lead.id)
        }
      }
    },



  }
}
</script>

<style scoped>
.badge{
  padding:4px 10px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  background:#f3f4f6;
}
.badge--green{ background:#14532d;color:#dcfce7;border-color:#14532d }
.badge--red{ background:#7f1d1d;color:#fee2e2;border-color:#7f1d1d }
.badge--blue{ background:#1f3b57;color:#cfe6ff;border-color:#1f3b57 }
.badge--amber{ background:#92400e;color:#fff7ed;border-color:#92400e }
.mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
</style>
